// Ambiance du raid selon l'heure et la météo : soleil (ou lune), ciel, exposition, halo des lumières (bloom),
// voile d'obscurité ou de brume autour du joueur, pluie, réverbères allumés la nuit, lampes torches.
import * as THREE from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { DEFAULT_COND, type RaidCond } from '../shared/atmos';
import { MAP } from '../shared/map';

interface Look {
  sun: number; sunI: number; sunDir: [number, number, number]; sky: number; ground: number; hemiI: number; bg: number; exposure: number; env: number;
  veil: [number, number, number, number] | null; // voile autour du joueur : couleur (r, g, b) et opacité au bord de l'écran
  veilNear: number; veilFar: number; // rayons (m) : dégagé jusqu'à near, voile complet à far
  bloom: number; windows: number; lamps: boolean; torch: boolean;
  zenith: number; horizon: number; // ciel (vue à la 3e personne) : haut et bas du dégradé
}

// Heures : jour franc, fin de journée (l'ambiance d'origine), nuit au clair de lune
const TIME: Record<RaidCond['time'], Look> = {
  day: { sun: 0xfff3e0, sunI: 2.85, sunDir: [-45, 95, -30], sky: 0xc3d6e6, ground: 0x4c4434, hemiI: 1.2, bg: 0x8ea3b2, exposure: 0.98, env: 0.25, veil: null, veilNear: 0, veilFar: 0, bloom: 0.18, windows: 0.4, lamps: false, torch: false, zenith: 0x4f7fb8, horizon: 0xc8d6e0 },
  dusk: { sun: 0xffc48a, sunI: 2.6, sunDir: [-75, 55, -48], sky: 0xa3b4c8, ground: 0x3d3226, hemiI: 1.15, bg: 0x2a2420, exposure: 1.1, env: 0.2, veil: [40, 28, 20, 0.28], veilNear: 14, veilFar: 34, bloom: 0.32, windows: 1.3, lamps: false, torch: false, zenith: 0x34456a, horizon: 0xd99a68 },
  night: { sun: 0x8fa8ff, sunI: 0.6, sunDir: [40, 80, -60], sky: 0x34466c, ground: 0x10141c, hemiI: 0.6, bg: 0x070a10, exposure: 1.0, env: 0.08, veil: [3, 6, 14, 0.72], veilNear: 8, veilFar: 22, bloom: 0.75, windows: 2.6, lamps: true, torch: true, zenith: 0x03050b, horizon: 0x141b2a },
};

export class Atmosphere {
  private composer: EffectComposer | null = null;
  private bloom: UnrealBloomPass | null = null;
  private cond: RaidCond = DEFAULT_COND;
  private look: Look = TIME.dusk;
  private veil: HTMLElement;
  private rain: THREE.LineSegments | null = null;
  private rainPos: Float32Array | null = null;
  private lampPool: THREE.PointLight[] = [];
  private bulbs: THREE.InstancedMesh | null = null;
  private torches: THREE.SpotLight[] = [];
  private windowMats: THREE.MeshStandardMaterial[] = [];
  private quality: 'basse' | 'moyenne' | 'haute' = 'haute';
  readonly lamps = MAP.props.filter(p => p.model === 'lamp').map(p => new THREE.Vector3(p.x, 4.1, p.y));
  pxPerM = 40; // pixels par mètre à l'écran (pour le voile)
  topDown = false; // voile autour du joueur (vue de dessus) ; en 3e personne, la brume fait ce travail
  // Ciel : grande sphère en dégradé autour de la caméra (sans brume ni profondeur)
  private sky = new THREE.Mesh(new THREE.SphereGeometry(600, 32, 16), new THREE.ShaderMaterial({
    uniforms: { top: { value: new THREE.Color() }, bottom: { value: new THREE.Color() } },
    vertexShader: 'varying vec3 vP; void main() { vP = position; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
    fragmentShader: `uniform vec3 top; uniform vec3 bottom; varying vec3 vP; void main() { float h = clamp(normalize(vP).y * 2.2, 0.0, 1.0); gl_FragColor = vec4(mix(bottom, top, pow(h, 0.7)), 1.0);
#include <tonemapping_fragment>
#include <colorspace_fragment>
}`,
    side: THREE.BackSide, depthWrite: false, fog: false,
  }));

  constructor(private renderer: THREE.WebGLRenderer, private scene: THREE.Scene, private camera: THREE.Camera,
    private hemi: THREE.HemisphereLight, private sun: THREE.DirectionalLight, private sunOffset: THREE.Vector3) {
    this.veil = document.createElement('div');
    this.veil.id = 'atmoVeil';
    document.getElementById('view')!.append(this.veil);
    this.sky.renderOrder = -10; this.sky.frustumCulled = false;
    this.sky.onBeforeRender = (_r, _s, cam) => this.sky.position.copy(cam.position);
    scene.add(this.sky);
    // Réverbères : quelques lumières réelles près du joueur, une ampoule lumineuse sur chacun
    for (let i = 0; i < 8; i++) { const l = new THREE.PointLight(0xffc98a, 0, 20, 1.3); this.lampPool.push(l); scene.add(l); }
    this.bulbs = new THREE.InstancedMesh(new THREE.SphereGeometry(0.22, 8, 6), new THREE.MeshStandardMaterial({ color: 0xffe2b0, emissive: 0xffc070, emissiveIntensity: 3 }), Math.max(1, this.lamps.length));
    this.lamps.forEach((p, i) => this.bulbs!.setMatrixAt(i, new THREE.Matrix4().makeTranslation(p.x, p.y, p.z)));
    this.bulbs.visible = false;
    scene.add(this.bulbs);
    // Lampes torches : la sienne et celles des plus proches
    for (let i = 0; i < 5; i++) {
      const s = new THREE.SpotLight(0xfff1d6, 0, 32, 0.42, 0.6, 2);
      scene.add(s, s.target);
      this.torches.push(s);
    }
  }

  // Lampes allumées aux fenêtres (matériau émissif du monde) : plus fortes la nuit
  attachWorld(root: THREE.Object3D) {
    this.windowMats = [];
    root.traverse(o => {
      const m = (o as THREE.Mesh).material as THREE.MeshStandardMaterial | undefined;
      if (m && m.emissive && m.emissive.getHex() === 0xffb45a && !this.windowMats.includes(m)) this.windowMats.push(m);
    });
    this.apply();
  }

  setQuality(q: 'basse' | 'moyenne' | 'haute') { this.quality = q; this.resize(); }

  set(cond: RaidCond | null) { this.cond = cond ?? DEFAULT_COND; this.apply(); }
  get current() { return this.cond; }

  private apply() {
    const L = { ...TIME[this.cond.time] };
    const w = this.cond.weather;
    // Météo : couvert (soleil voilé, lumière grise), pluie (plus sombre), brouillard (voile gris)
    const grey = (c: number, k: number) => new THREE.Color(c).lerp(new THREE.Color(0x8c9196), k).getHex();
    if (w === 'overcast' || w === 'rain') {
      L.sunI *= w === 'rain' ? 0.32 : 0.45; L.sun = grey(L.sun, 0.6); L.sky = grey(L.sky, 0.5); L.hemiI *= w === 'rain' ? 1.0 : 1.12;
      L.exposure *= w === 'rain' ? 0.9 : 0.97; L.bg = grey(L.bg, 0.4);
      if (this.cond.time !== 'night') L.veil = w === 'rain' ? [52, 60, 68, 0.32] : [70, 74, 78, 0.18];
      L.veilNear = this.cond.time === 'night' ? L.veilNear : 12; L.veilFar = this.cond.time === 'night' ? L.veilFar : 30;
    }
    if (w === 'fog') {
      L.sunI *= 0.5; L.sky = grey(L.sky, 0.6);
      L.veil = this.cond.time === 'night' ? [10, 13, 18, 0.9] : [170, 176, 180, 0.88];
      L.veilNear = 5; L.veilFar = 15;
    }
    this.look = L;
    this.sun.color.setHex(L.sun); this.sun.intensity = L.sunI;
    this.sunOffset.set(...L.sunDir);
    this.hemi.color.setHex(L.sky); this.hemi.groundColor.setHex(L.ground); this.hemi.intensity = L.hemiI;
    // Lointain : brume à la couleur du voile (nuit : obscurité, brouillard : gris), légère brume de distance sinon
    // Le ciel se fond dans la brume à l'horizon ; temps couvert et pluie le grisent, le brouillard l'efface
    const top = new THREE.Color(L.zenith), hor = new THREE.Color(L.horizon);
    if (w === 'overcast' || w === 'rain') { top.lerp(new THREE.Color(0x6c747c), w === 'rain' ? 0.75 : 0.6).multiplyScalar(this.cond.time === 'night' ? 0.25 : 1); hor.lerp(new THREE.Color(0x9aa1a8), 0.7).multiplyScalar(this.cond.time === 'night' ? 0.2 : w === 'rain' ? 0.75 : 0.9); }
    const thick = w === 'fog' || w === 'rain';
    const fogCol = thick && L.veil ? new THREE.Color(`rgb(${L.veil[0]},${L.veil[1]},${L.veil[2]})`) : hor.clone();
    if (w === 'fog') { top.copy(fogCol); hor.copy(fogCol); }
    const near = thick || this.cond.time === 'night' ? L.veilNear * 1.8 : 70, far = thick || this.cond.time === 'night' ? L.veilFar * (2.4 + 2 * (1 - (L.veil?.[3] ?? 0.5))) : this.cond.time === 'dusk' ? 260 : 380;
    this.scene.fog = new THREE.Fog(fogCol, near, far);
    (this.sky.material as THREE.ShaderMaterial).uniforms.top!.value.copy(top);
    (this.sky.material as THREE.ShaderMaterial).uniforms.bottom!.value.copy(hor);
    this.scene.background = hor;
    this.renderer.toneMappingExposure = L.exposure;
    this.scene.environmentIntensity = L.env;
    for (const m of this.windowMats) m.emissiveIntensity = L.windows;
    if (this.bulbs) this.bulbs.visible = L.lamps;
    if (this.bloom) this.bloom.strength = L.bloom;
    this.setRain(w === 'rain');
  }

  // ---------------------------------------------------------------- Pluie : traits qui tombent autour de la caméra

  private setRain(on: boolean) {
    if (on && !this.rain) {
      const N = 1400;
      this.rainPos = new Float32Array(N * 6);
      for (let i = 0; i < N; i++) this.resetDrop(i, 0, 0, true);
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.BufferAttribute(this.rainPos, 3));
      this.rain = new THREE.LineSegments(g, new THREE.LineBasicMaterial({ color: 0xb8c6d4, transparent: true, opacity: 0.38, depthWrite: false }));
      this.rain.frustumCulled = false;
      this.scene.add(this.rain);
    }
    if (this.rain) this.rain.visible = on;
  }
  private resetDrop(i: number, cx: number, cz: number, anyHeight = false) {
    const p = this.rainPos!, x = cx + (Math.random() - 0.5) * 60, z = cz + (Math.random() - 0.5) * 60, y = anyHeight ? Math.random() * 26 : 22 + Math.random() * 4;
    p.set([x, y, z, x + 0.12, y + 0.9, z + 0.06], i * 6);
  }

  // ---------------------------------------------------------------- Chaque image

  // player : position du joueur (monde) et sa place à l'écran ; torches : porteurs de lampe (le joueur d'abord)
  frame(dt: number, cam: { x: number; z: number }, player: { x: number; z: number; sx: number; sy: number } | null, torches: { x: number; z: number; angle: number }[]) {
    const L = this.look;
    // Voile : dégagé autour du joueur, de plus en plus dense au loin (nuit : obscurité, brouillard : gris)
    if (L.veil && player && this.topDown) {
      const [r, g, b, a] = L.veil, n = L.veilNear * this.pxPerM, f = L.veilFar * this.pxPerM;
      this.veil.style.background = `radial-gradient(circle at ${player.sx.toFixed(0)}px ${player.sy.toFixed(0)}px, rgba(${r},${g},${b},0) ${n.toFixed(0)}px, rgba(${r},${g},${b},${(a * 0.6).toFixed(2)}) ${((n + f) / 2).toFixed(0)}px, rgba(${r},${g},${b},${a}) ${f.toFixed(0)}px)`;
      this.veil.style.display = 'block';
    } else this.veil.style.display = 'none';
    // Pluie
    if (this.rain?.visible && this.rainPos) {
      const p = this.rainPos, fall = dt * 24;
      for (let i = 0; i < p.length / 6; i++) {
        const k = i * 6;
        p[k + 1]! -= fall; p[k + 4]! -= fall; p[k]! -= fall * 0.12; p[k + 3]! -= fall * 0.12;
        if (p[k + 1]! < 0) this.resetDrop(i, cam.x, cam.z);
      }
      this.rain.geometry.attributes.position!.needsUpdate = true;
    }
    // Réverbères proches allumés (la nuit)
    // (autour de la caméra : c'est là qu'on regarde)
    const near = L.lamps ? [...this.lamps].sort((a, b) => Math.hypot(a.x - cam.x, a.z - cam.z) - Math.hypot(b.x - cam.x, b.z - cam.z)) : [];
    this.lampPool.forEach((l, i) => { const p = near[i]; l.intensity = p ? 70 : 0; if (p) l.position.copy(p); });
    // Lampes torches (la nuit) : cône devant chaque porteur, à hauteur de poitrine
    this.torches.forEach((s, i) => {
      const t = L.torch ? torches[i] : undefined;
      s.intensity = t ? (i === 0 ? 120 : 60) : 0;
      if (!t) return;
      s.position.set(t.x + Math.cos(t.angle) * 0.4, 1.35, t.z + Math.sin(t.angle) * 0.4);
      s.target.position.set(t.x + Math.cos(t.angle) * 9, 0, t.z + Math.sin(t.angle) * 9);
    });
  }

  // ---------------------------------------------------------------- Rendu : halo des lumières en qualité haute

  render() {
    if (this.quality === 'haute') {
      if (!this.composer) this.makeComposer();
      this.composer!.render();
    } else this.renderer.render(this.scene, this.camera);
  }
  private makeComposer() {
    const size = this.renderer.getSize(new THREE.Vector2());
    // Rendu intermédiaire multi-échantillonné : sans lui, le halo (bloom) fait perdre l'anticrénelage
    this.composer = new EffectComposer(this.renderer, new THREE.WebGLRenderTarget(size.x, size.y, { type: THREE.HalfFloatType, samples: 4 }));
    this.composer.addPass(new RenderPass(this.scene, this.camera));
    this.bloom = new UnrealBloomPass(new THREE.Vector2(size.x, size.y), this.look.bloom, 0.55, 0.82);
    this.composer.addPass(this.bloom);
    this.composer.addPass(new OutputPass());
    this.resize();
  }
  resize() {
    if (!this.composer) return;
    const size = this.renderer.getSize(new THREE.Vector2());
    this.composer.setPixelRatio(this.renderer.getPixelRatio());
    this.composer.setSize(size.x, size.y);
  }
}
